import {
	Box,
	Button,
	Flex,
	FormControl,
	FormLabel,
	Icon,
	Input,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	Spinner,
	Switch,
	Table,
	Tbody,
	Td,
	Text,
	Th,
	Thead,
	Tooltip,
	Tr,
	useColorModeValue,
	useDisclosure
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { MdGroupAdd, MdDelete, MdEdit } from 'react-icons/md';
import Card from '../../components/card/Card';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { RootState } from '../../store/store';
import {
	createIndicator,
	editIndicator,
	fetchIndicators,
	removeIndicator,
	toggleIndicatorActive
} from '../../store/features/indicator/indicator.slice';
import { Indicator } from '../../models/indicator.model';
import { Role } from '../../models/user.model';
import { toastService } from '../../services/toast.service';

export default function Indicadores() {
	const dispatch = useAppDispatch();
	const { list, loading } = useAppSelector((state: RootState) => state.indicator);
	const { role } = useAppSelector((state: RootState) => state.auth);
	const textColor = useColorModeValue('navy.700', 'white');
	const subColor = useColorModeValue('secondaryGray.700', 'secondaryGray.500');

	const isAdmin = role === Role.ADMIN;

	const createModal = useDisclosure();
	const editModal = useDisclosure();
	const deleteModal = useDisclosure();

	const [selected, setSelected] = useState<Indicator | null>(null);
	const [form, setForm] = useState({ name: '', phone: '', pixKey: '' });

	useEffect(() => {
		dispatch(fetchIndicators());
	}, [dispatch]);

	const openCreate = () => {
		setForm({ name: '', phone: '', pixKey: '' });
		createModal.onOpen();
	};

	const handleCreate = async () => {
		if (!form.name.trim()) {
			toastService.warning('Informe o nome do indicador');
			return;
		}
		await dispatch(createIndicator({ name: form.name.trim(), phone: form.phone, pixKey: form.pixKey }));
		createModal.onClose();
	};

	const openEdit = (indicator: Indicator) => {
		setSelected(indicator);
		setForm({ name: indicator.name, phone: indicator.phone || '', pixKey: indicator.pixKey || '' });
		editModal.onOpen();
	};

	const handleEdit = async () => {
		if (!selected) return;
		if (!form.name.trim()) {
			toastService.warning('Informe o nome do indicador');
			return;
		}
		await dispatch(editIndicator({ id: selected.id, name: form.name.trim(), phone: form.phone, pixKey: form.pixKey }));
		editModal.onClose();
	};

	const openDelete = (indicator: Indicator) => {
		setSelected(indicator);
		deleteModal.onOpen();
	};

	const handleDelete = async () => {
		if (!selected) return;
		await dispatch(removeIndicator({ id: selected.id }));
		deleteModal.onClose();
	};

	return (
		<Box pt='10px'>
			<Card>
				<Flex justify='space-between' align='center' mb='16px'>
					<Text fontWeight='800' fontSize='lg' color={textColor}>
						Indicadores
					</Text>
					{isAdmin && (
						<Button variant='brand' leftIcon={<Icon as={MdGroupAdd} />} onClick={openCreate}>
							Novo indicador
						</Button>
					)}
				</Flex>

				{loading && list.length === 0 && <Spinner color='brand.500' />}

				<Box overflowX='auto'>
					<Table variant='simple' size='sm'>
						<Thead>
							<Tr>
								<Th>Nome</Th>
								<Th>Telefone</Th>
								<Th>Chave PIX</Th>
								<Th isNumeric>Clientes</Th>
								<Th>Ativo</Th>
								{isAdmin && <Th>Ações</Th>}
							</Tr>
						</Thead>
						<Tbody>
							{list.map(indicator => (
								<Tr key={indicator.id}>
									<Td fontWeight='600' color={textColor}>
										{indicator.name}
									</Td>
									<Td color={subColor}>{indicator.phone || '—'}</Td>
									<Td color={subColor}>{indicator.pixKey || '—'}</Td>
									<Td isNumeric color={textColor}>
										{indicator.clientes ?? 0}
									</Td>
									<Td>
										<Switch
											variant='main'
											isChecked={indicator.active}
											isDisabled={!isAdmin}
											onChange={() => dispatch(toggleIndicatorActive({ id: indicator.id, active: !indicator.active }))}
											colorScheme='blue'
										/>
									</Td>
									{isAdmin && (
										<Td>
											<Flex gap='6px'>
												<Tooltip label='Editar' fontSize='xs'>
													<Button size='xs' variant='light' onClick={() => openEdit(indicator)}>
														<Icon as={MdEdit} />
													</Button>
												</Tooltip>
												<Tooltip label='Excluir' fontSize='xs'>
													<Button size='xs' variant='light' color='red.500' onClick={() => openDelete(indicator)}>
														<Icon as={MdDelete} />
													</Button>
												</Tooltip>
											</Flex>
										</Td>
									)}
								</Tr>
							))}
							{!loading && list.length === 0 && (
								<Tr>
									<Td colSpan={6}>
										<Text color={subColor} fontSize='sm'>
											Nenhum indicador cadastrado.
										</Text>
									</Td>
								</Tr>
							)}
						</Tbody>
					</Table>
				</Box>
			</Card>

			{/* Create / Edit modal (mesmo formulário) */}
			<Modal
				isOpen={createModal.isOpen || editModal.isOpen}
				onClose={createModal.isOpen ? createModal.onClose : editModal.onClose}
				isCentered>
				<ModalOverlay />
				<ModalContent borderRadius='20px'>
					<ModalHeader>{createModal.isOpen ? 'Novo indicador' : `Editar — ${selected?.name}`}</ModalHeader>
					<ModalCloseButton />
					<ModalBody>
						<FormControl mb='12px'>
							<FormLabel fontSize='sm'>Nome</FormLabel>
							<Input variant='main' value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
						</FormControl>
						<FormControl mb='12px'>
							<FormLabel fontSize='sm'>Telefone de contato</FormLabel>
							<Input
								variant='main'
								placeholder='(11) 90000-0000'
								value={form.phone}
								onChange={e => setForm({ ...form, phone: e.target.value })}
							/>
						</FormControl>
						<FormControl>
							<FormLabel fontSize='sm'>Chave PIX</FormLabel>
							<Input
								variant='main'
								placeholder='CPF, e-mail, telefone ou chave aleatória'
								value={form.pixKey}
								onChange={e => setForm({ ...form, pixKey: e.target.value })}
							/>
						</FormControl>
					</ModalBody>
					<ModalFooter gap='10px'>
						<Button variant='light' onClick={createModal.isOpen ? createModal.onClose : editModal.onClose}>
							Cancelar
						</Button>
						<Button variant='brand' onClick={createModal.isOpen ? handleCreate : handleEdit}>
							{createModal.isOpen ? 'Criar' : 'Salvar'}
						</Button>
					</ModalFooter>
				</ModalContent>
			</Modal>

			{/* Delete confirmation */}
			<Modal isOpen={deleteModal.isOpen} onClose={deleteModal.onClose} isCentered>
				<ModalOverlay />
				<ModalContent borderRadius='20px'>
					<ModalHeader>Excluir indicador</ModalHeader>
					<ModalCloseButton />
					<ModalBody>
						<Text color={textColor}>
							Excluir <strong>{selected?.name}</strong>?
						</Text>
						<Text fontSize='sm' color={subColor} mt='10px'>
							Os clientes já vinculados a este indicador continuam existindo; só o vínculo deixa de aparecer em novos relatórios.
						</Text>
					</ModalBody>
					<ModalFooter gap='10px'>
						<Button variant='light' onClick={deleteModal.onClose}>
							Cancelar
						</Button>
						<Button colorScheme='red' onClick={handleDelete}>
							Excluir
						</Button>
					</ModalFooter>
				</ModalContent>
			</Modal>
		</Box>
	);
}
